import { ColorPalette } from './structure';

/**
 * The Foresight New UI palette under the app's token names: each numeric step is the nearest
 * step of the brand ladder (navy, purple, blue, grey and the status hues), so some names share
 * a value (primary[60] = [50], [45] = [40], [30] = [20]; secondary[50] = [40]; neutral[2] = [5]).
 * The semantic groups carry the roles; prefer them over a numeric step wherever a colour has one.
 */
const navy = '#181630';
const purple700 = '#5C5388';
const purple800 = '#423B5C';
const hairline = '#E9E9F2';
const white = '#FFFFFF';

export const newUiColors: ColorPalette = {
  primary: {
    100: purple800,
    95: navy,
    90: purple700,
    80: '#7366AF',
    70: '#8A79D6',
    60: '#A18DFD', // the accent fill; as text use text.link, as an icon icon.link
    50: '#A18DFD',
    45: '#C1B4FE',
    40: '#C1B4FE',
    30: '#D6CEFE',
    20: '#D6CEFE',
    10: '#EBE6FF', // the hover and selected tint
    5: '#F3F1FF',
    0: white,
  },
  secondary: {
    90: purple800,
    80: purple700,
    70: '#7366AF',
    60: '#8A79D6',
    50: '#A18DFD',
    40: '#A18DFD',
    30: '#C1B4FE',
    20: '#D6CEFE',
    10: '#EBE6FF',
    5: '#F3F1FF',
  },
  tertiary: {
    90: '#133E84',
    80: '#305EAB',
    70: '#406FBE',
    60: '#5785D1',
    50: '#78A1E2',
    40: '#9EC0F7',
    30: '#C0D8FF',
    20: '#DEEAFF',
    10: '#EEF5FF',
  },
  neutral: {
    80: hairline,
    70: '#364257',
    60: '#64647A',
    50: '#818296',
    40: '#A0A0B8',
    30: '#BDBDD0',
    20: '#D8D8E8',
    10: hairline,
    5: '#F5F5F9',
    2: '#F5F5F9',
  },
  critical: {
    60: '#A73B3C',
    50: '#BB4C4C',
    40: '#CF6462',
    30: '#E38682',
    20: '#F8AAA5',
    10: '#FFCAC6',
    5: '#FFF1F0',
  },
  warning: {
    70: '#885500',
    60: '#9A6500',
    50: '#C39957',
    40: '#DCB984',
    30: '#EED3AA',
    20: '#FAE7CB',
    10: '#FFF2E0',
  },
  success: {
    70: '#004C20',
    60: '#258044',
    50: '#43955A',
    40: '#6CAE7B',
    30: '#B7E0BF',
    20: '#D4F1D9',
    10: '#E5F9E8',
  },
  gray: {
    700: navy,
  },
  text: {
    main: navy,
    secondary: '#64647A',
    onNavigation: '#F0EDE8',
    secondaryOnNavigation: '#A0A0B8',
    link: purple700,
    tabSelected: navy,
    tab: '#6E6F84',
    disabled: '#A0A0B8',
  },
  border: {
    default: hairline,
    input: '#BDBDD0',
    tabIndicator: navy,
    disabled: '#D8D8E8',
    navigation: purple800,
    card: hairline,
    cardHover: '#D6CEFE',
    cardSelected: '#C1B4FE',
  },
  surface: {
    app: '#F5F5F9',
    card: white,
    cardNested: white,
    navigation: navy,
    navigationPanel: navy,
    navigationSelected: purple700,
    navigationHover: purple800,
    navigationField: purple800,
    badge: purple800,
    selectedSubtle: '#F3F1FF',
    // neutral[5] at 70% on white: the ladder's lightest grey is the page background, too strong for a stripe
    hover: '#F8F8FB',
    disabled: hairline,
    tableStripe: '#F8F8FB',
    tableTotal: '#F3F1FF',
    cardHover: '#F3F1FF',
    cardSelected: '#EBE6FF',
  },
  icon: {
    main: navy,
    secondary: '#64647A',
    link: purple700,
  },
  tint: {
    blue: '#DEEAFF',
    blueForeground: '#305EAB',
    purple: '#EBE6FF',
    purpleForeground: purple700,
    yellow: '#FAE7CB',
    yellowForeground: '#885500',
    red: '#FFE3E1',
    redForeground: '#A73B3C',
    green: '#D4F1D9',
    greenForeground: '#096F34',
    grey: hairline,
    greyForeground: '#64647A',
  },
  // Each format family's own branding, so a document type is recognised at a glance.
  fileType: {
    pdf: '#BD0100',
    presentation: '#D44519',
    sheet: '#1B7343',
    word: '#27549B',
    other: '#A18DFD', // secondary[50]
  },
};
